import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Users, UserPlus, ClipboardList, MapPin, LogOut, Settings,
  TrendingUp, AlertCircle, CheckCircle, Clock, Activity,
  Shield, FileText
} from 'lucide-react';
import api, { getCurrentUser, logout } from '../utils/api';
import LiveMap from '../components/LiveMap';

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [staff, setStaff] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const currentUser = getCurrentUser();
    if (!currentUser || currentUser.role !== 'super_admin') {
      navigate('/field-staff/login');
      return;
    }
    setUser(currentUser);
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch stats
      const statsRes = await api.get('/api/field-staff/super-admin/reports/dashboard-stats');
      setStats(statsRes.data);
      
      // Fetch admins
      const adminsRes = await api.get('/api/field-staff/super-admin/admins');
      setAdmins(adminsRes.data);
      
      // Fetch staff
      const staffRes = await api.get('/api/field-staff/admin/staff');
      setStaff(staffRes.data);
      
      // Fetch tasks
      const tasksRes = await api.get('/api/field-staff/admin/tasks?limit=10');
      setTasks(tasksRes.data);
      
      // Fetch performance
      const perfRes = await api.get('/api/field-staff/super-admin/reports/staff-performance');
      setPerformance(perfRes.data);
      
      // Fetch activity logs
      const logsRes = await api.get('/api/field-staff/super-admin/activity-logs?limit=20');
      setActivityLogs(logsRes.data);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, subtext }) => (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className={`text-3xl font-bold mt-2 ${color}`}>{value}</p>
            {subtext && <p className="text-xs text-gray-500 mt-1">{subtext}</p>}
          </div>
          <div className={`p-3 rounded-full ${color} bg-opacity-10`}>
            <Icon className="h-8 w-8" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Activity className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="h-6 w-6" />
              <div>
                <h1 className="text-xl font-bold">Super Admin Dashboard</h1>
                <p className="text-sm text-blue-100">Welcome, {user?.name}</p>
              </div>
            </div>
            <Button onClick={logout} variant="ghost" className="text-white hover:bg-white/20">
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Total Staff"
            value={stats?.total_staff || 0}
            icon={Users}
            color="text-blue-600"
            subtext={`${stats?.active_staff || 0} active`}
          />
          <StatCard
            title="Total Tasks"
            value={stats?.total_tasks || 0}
            icon={ClipboardList}
            color="text-green-600"
            subtext={`${stats?.completed_tasks || 0} completed`}
          />
          <StatCard
            title="Overdue Tasks"
            value={stats?.overdue_tasks || 0}
            icon={AlertCircle}
            color="text-red-600"
            subtext={`${stats?.tasks_due_today || 0} due today`}
          />
          <StatCard
            title="Unverified Remarks"
            value={stats?.unverified_remarks || 0}
            icon={Clock}
            color="text-orange-600"
            subtext="Need review"
          />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="admins">Admins ({admins.length})</TabsTrigger>
            <TabsTrigger value="staff">Staff ({staff.length})</TabsTrigger>
            <TabsTrigger value="tasks">Tasks</TabsTrigger>
            <TabsTrigger value="map">Live Map</TabsTrigger>
            <TabsTrigger value="logs">Activity Logs</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Task Status Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Task Status Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">New</span>
                      <Badge>{stats?.new_tasks || 0}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">In Progress</span>
                      <Badge variant="secondary">{stats?.in_progress_tasks || 0}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Completed</span>
                      <Badge className="bg-green-600">{stats?.completed_tasks || 0}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Resolved</span>
                      <Badge className="bg-blue-600">{stats?.resolved_tasks || 0}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Closed</span>
                      <Badge variant="outline">{stats?.closed_tasks || 0}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Top Performers */}
              <Card>
                <CardHeader>
                  <CardTitle>Top Performers</CardTitle>
                  <CardDescription>Based on completion rate</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {performance.slice(0, 5).map((perf, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{perf.staff_name}</p>
                          <p className="text-xs text-gray-500">{perf.completed} / {perf.total_assigned} tasks</p>
                        </div>
                        <Badge className="bg-green-600">{perf.completion_rate}%</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Admins Tab */}
          <TabsContent value="admins">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Admin Users</CardTitle>
                  <Button onClick={() => navigate('/field-staff/super-admin/create-admin')}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Create Admin
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {admins.map((admin) => (
                    <div key={admin.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                      <div>
                        <p className="font-medium">{admin.name}</p>
                        <p className="text-sm text-gray-600">{admin.email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge>{admin.role}</Badge>
                        {admin.is_active ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Staff Tab */}
          <TabsContent value="staff">
            <Card>
              <CardHeader>
                <CardTitle>Staff Members</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {staff.map((member) => (
                    <div key={member.id} className="border rounded-lg p-4 hover:shadow-md transition">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium">{member.name}</p>
                          <p className="text-sm text-gray-600">{member.position}</p>
                        </div>
                        {member.is_active && <Badge className="bg-green-600">Active</Badge>}
                      </div>
                      <p className="text-xs text-gray-500">{member.email}</p>
                      <p className="text-xs text-gray-500">{member.mobile}</p>
                      {member.area_covered && (
                        <p className="text-xs text-gray-500 mt-1">
                          <MapPin className="h-3 w-3 inline mr-1" />
                          {member.area_covered}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks">
            <Card>
              <CardHeader>
                <CardTitle>Recent Tasks</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {tasks.map((task) => (
                    <div key={task.id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{task.title}</p>
                          <p className="text-sm text-gray-600">{task.description}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Assigned to: {task.assigned_to_name}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <Badge>{task.status}</Badge>
                          <Badge variant="outline">{task.priority}</Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Map Tab */}
          <TabsContent value="map">
            <LiveMap />
          </TabsContent>

          {/* Activity Logs Tab */}
          <TabsContent value="logs">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Activity Logs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {activityLogs.map((log) => (
                    <div key={log.id} className="border-l-4 border-blue-600 pl-4 py-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-sm font-medium">{log.user_name} ({log.user_role})</p>
                          <p className="text-sm text-gray-600">{log.action} on {log.resource_type}</p>
                        </div>
                        <span className="text-xs text-gray-500">
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
